/* v8 ignore start */
import {createSelector, createSlice} from "@reduxjs/toolkit";

import {Tracking} from "@/lib/analytics/mixpanel";
import {AnimatedProjectModel} from "@/models/animatedProjectModel";
import {MusicTrackModel} from "@/models/musicTrackModel";
import {PageGroupModel} from "@/models/pageGroupModel";
import {ProjectModel} from "@/models/projectModel";
import {TRootState} from "@/store";
import {
  fetchAnimatedProject,
  fetchAnimatedProjectDetails,
  reCreateProject,
  updateAnimatedProjectName,
} from "@/store/slices/animatedProjectSlice/thunks";

type TRequestStatus = "idle" | "loading" | "succeeded" | "failed";

export interface IAnimateProjectDetails {
  project: ProjectModel.IJson;
  pageGroups: PageGroupModel.IJson[];
}

interface IAnimatedProjectState {
  project: Nullable<AnimatedProjectModel.IJson>;
  details: Nullable<IAnimateProjectDetails>;
  projectViewLink?: string;
  inviteeEmails: string[];
  fetchStatus: TRequestStatus;
  fetchDetailsStatus: TRequestStatus;
  updateStatus: TRequestStatus;
  fetchError?: string;
  fetchDetailsError?: string;
  pendingMusicTrackId?: number;
}

const initialState: IAnimatedProjectState = {
  project: null,
  details: null,
  projectViewLink: undefined,
  inviteeEmails: [],
  fetchStatus: "idle",
  fetchDetailsStatus: "idle",
  updateStatus: "idle",
  fetchError: undefined,
  fetchDetailsError: undefined,
  pendingMusicTrackId: undefined,
};

const animatedProjectSlice = createSlice({
  name: "animatedProject",
  initialState,
  reducers: {
    autoCreateHasFinished: (state, action) => {
      state.project = action.payload;
      state.updateStatus = "succeeded";
    },
    viewPermissionUpdated: (state, action) => {
      if (state.project) {
        state.projectViewLink = action.payload;
      }
    },
    viewerAdded: (state, action) => {
      state.inviteeEmails.unshift(action.payload);
    },
    viewerRemoved: (state, action) => {
      state.inviteeEmails = state.inviteeEmails.filter((email) => email !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnimatedProject.pending, (state) => {
        state.fetchStatus = "loading";
        state.fetchError = undefined;
      })
      .addCase(fetchAnimatedProject.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.fetchError = undefined;
        state.project = action.payload;
        state.projectViewLink = action.payload.viewLink;
        if (action.payload.movieInvites)
          state.inviteeEmails = action.payload.movieInvites
            .sort((a, b) => b.id - a.id)
            .map((invite) => invite.inviteeEmail);
      })
      .addCase(fetchAnimatedProject.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.fetchError = action.error.message as string;
      })
      .addCase(fetchAnimatedProjectDetails.pending, (state) => {
        state.fetchDetailsStatus = "loading";
        state.fetchDetailsError = undefined;
      })
      .addCase(fetchAnimatedProjectDetails.fulfilled, (state, action) => {
        state.fetchDetailsStatus = "succeeded";
        state.fetchDetailsError = undefined;
        state.details = action.payload;
      })
      .addCase(fetchAnimatedProjectDetails.rejected, (state, action) => {
        state.fetchDetailsStatus = "failed";
        state.fetchDetailsError = action.error.message as string;
      })
      .addCase(reCreateProject.pending, (state, action) => {
        state.updateStatus = "loading";
        state.pendingMusicTrackId = action.meta.arg.musicTrackId;
      })
      .addCase(reCreateProject.fulfilled, (state, action) => {
        Tracking.track("MemoryExplorer_RecreateProjectFinished");
        state.updateStatus = "succeeded";
        state.project = action.payload;
        state.pendingMusicTrackId = undefined;
      })
      .addCase(reCreateProject.rejected, (state) => {
        Tracking.track("MemoryExplorer_RecreateProjectFailed");
        state.updateStatus = "failed";
        state.pendingMusicTrackId = undefined;
      })
      .addCase(updateAnimatedProjectName.pending, (state) => {
        state.updateStatus = "loading";
      })
      .addCase(updateAnimatedProjectName.fulfilled, (state, action) => {
        if (state.project) {
          state.project.name = action.payload.name;
          state.project.segments[0] = action.payload.segment;
        }
        state.updateStatus = "succeeded";
      })
      .addCase(updateAnimatedProjectName.rejected, (state) => {
        state.updateStatus = "failed";
      });
  },
});

const selectProjectOrThrow = (state: TRootState) => {
  const project = state.animatedProject.project;

  if (!project) throw new Error("missing project, trying to access properties before project is present");

  return project;
};

const selectProjectModelOrThrow = createSelector(
  selectProjectOrThrow,
  (animatedProjectJson) => new AnimatedProjectModel(animatedProjectJson)
);

export const selectAnimatedProjectPresence = (state: TRootState) => !!state.animatedProject.project;
export const selectAnimatedProjectFetchStatus = (state: TRootState) => state.animatedProject.fetchStatus;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectAnimatedProjectDetailsFetchStatus = (state: TRootState) => state.animatedProject.fetchDetailsStatus;
export const selectAnimatedProjectUpdateStatus = (state: TRootState) => state.animatedProject.updateStatus;
export const selectAnimatedProjectViewLink = (state: TRootState) => state.animatedProject.projectViewLink;
export const selectAnimatedProjectInviteeEmails = (state: TRootState) => state.animatedProject.inviteeEmails;
export const selectAnimatedProjectName = (state: TRootState) => selectProjectOrThrow(state).name;
export const selectAnimatedProjectId = (state: TRootState) => selectProjectOrThrow(state).id;
export const selectAnimatedProjectIsTextEnabled = (state: TRootState) => selectProjectOrThrow(state).isTextEnabled;
export const selectAnimatedProjectDistributionStrategy = (state: TRootState) =>
  selectProjectOrThrow(state).distributionStrategy;
export const selectAnimatedProjectMusicTrack = (state: TRootState) => selectProjectOrThrow(state).musicTrack;
export const selectAnimatedProjectPendingMusicTrackId = (state: TRootState) =>
  state.animatedProject.pendingMusicTrackId;

export const selectAnimatedProjectMusicTrackModel = createSelector(
  selectAnimatedProjectMusicTrack,
  (musicTrack) => new MusicTrackModel(musicTrack)
);

export const selectLottieAnimation = createSelector(
  selectProjectOrThrow,
  (project) => new AnimatedProjectModel(project).lottieAnimation().deepClonedObject() // deep copy is necessary since react-lottie-player changes its given object
);

const RESERVED_SEGMENTS_COUNT = 2; // intro and outro
const ACCEPTABLE_DEVIATION_IN_SECONDS = 4; // there can be silence at the end of animation to ensure outro logo has a reserved

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectHasUsedAllPhotos = createSelector(
  selectProjectOrThrow,
  (state: TRootState) =>
    state.animatedProject.details?.pageGroups.reduce(
      (sum, pageGroup) => pageGroup.photoGroups.reduce((sum, photoGroup) => photoGroup.photoIds.length + sum, 0) + sum,
      0
    ),
  (animatedProject, photoGroupPhotoCount) =>
    photoGroupPhotoCount === undefined
      ? false
      : animatedProject.segments.length - RESERVED_SEGMENTS_COUNT === photoGroupPhotoCount
);

export const selectIsAnimationLongerThanMusicDuration = createSelector(
  selectProjectModelOrThrow,
  selectAnimatedProjectMusicTrackModel,
  (animatedProject, musicTrack) =>
    animatedProject.durationInSeconds - ACCEPTABLE_DEVIATION_IN_SECONDS > musicTrack.durationInSeconds
);

export const {autoCreateHasFinished, viewPermissionUpdated, viewerAdded, viewerRemoved} = animatedProjectSlice.actions;

export default animatedProjectSlice.reducer;
/* v8 ignore stop */
